import { formatDistanceStrict } from 'date-fns';

export { formatRtp } from './rtp';

/** One locale for every number and date, so separators never vary between screens. */
export const LOCALE = 'en-GB';
/** en-GB compacts to `1.2m` / `4.2bn`; dashboards read `1.2M` / `4.2B` (K/M/B) far more reliably. */
const COMPACT_LOCALE = 'en-US';
export const EMPTY = '—';

/** Analytics are always UTC; other timestamps follow the user's preference (spec §6.7). */
export type TimeZonePref = 'utc' | 'local';

export type Numeric = number | bigint | string | null | undefined;
type DecimalString = `${number}`;

const DECIMAL = /^([+-]?)(\d+)(?:\.(\d+))?$/;

/** ISO 4217 codes this Intl build knows. `BTC` or `XMR` look like ISO codes but aren't. */
const ISO_CURRENCIES = new Set(Intl.supportedValuesOf('currency'));

const formatters = new Map<string, Intl.NumberFormat>();
function numberFormat(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options);
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat(options.notation === 'compact' ? COMPACT_LOCALE : LOCALE, options);
    formatters.set(key, f);
  }
  return f;
}

/** A finite decimal string for the value, or null. Accepts BIGINT/DECIMAL strings exactly. */
function toDecimalString(value: Numeric): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'number') return Number.isFinite(value) && DECIMAL.test(String(value)) ? String(value) : null;
  const s = value.trim();
  return DECIMAL.test(s) ? s : null;
}

/**
 * Integer cents (as the API sends them: numbers, BIGINT strings, or DECIMAL strings) to a
 * major-unit decimal string, shifting the point instead of dividing so nothing rounds.
 */
export function centsToMajor(cents: Numeric): DecimalString | null {
  const match = DECIMAL.exec(toDecimalString(cents) ?? '');
  if (!match) return null;
  const [, sign = '', whole = '', frac = ''] = match;
  const digits = whole + frac;
  const point = whole.length - 2;
  let out = point > 0 ? `${digits.slice(0, point)}.${digits.slice(point)}` : `0.${'0'.repeat(-point)}${digits}`;
  out = out.replace(/^0+(?=\d)/, '');
  const isZero = !/[1-9]/.test(out);
  return `${sign === '-' && !isZero ? '-' : ''}${out}` as DecimalString;
}

export interface MoneyOptions {
  /** `€28.9K` instead of `€28,900.00`. */
  compact?: boolean;
  /** `exceptZero` for deltas (`+€5.00`). */
  signDisplay?: Intl.NumberFormatOptions['signDisplay'];
  fractionDigits?: number;
}

/**
 * Money from integer cents in the given currency. ISO codes get their symbol; anything Intl
 * can't format (crypto such as `USDT`, social coins such as `GC.`) gets the code as a suffix.
 */
export function formatMoney(cents: Numeric, currency: string | null | undefined, options: MoneyOptions = {}): string {
  const major = centsToMajor(cents);
  if (major === null) return EMPTY;
  const { compact = false, signDisplay, fractionDigits } = options;
  const digits: Intl.NumberFormatOptions = compact
    ? { notation: 'compact', maximumFractionDigits: fractionDigits ?? 1 }
    : { minimumFractionDigits: fractionDigits ?? 2, maximumFractionDigits: fractionDigits ?? 2 };
  const base: Intl.NumberFormatOptions = signDisplay ? { ...digits, signDisplay } : digits;
  const code = currency?.trim() ?? '';
  if (ISO_CURRENCIES.has(code)) {
    try {
      return numberFormat({ ...base, style: 'currency', currency: code, currencyDisplay: 'symbol' }).format(major);
    } catch {
      // Unknown to this Intl build: fall through to the suffix form.
    }
  }
  const amount = numberFormat(base).format(major);
  return code ? `${amount} ${code}` : amount;
}

/**
 * Plain numbers. `fractionDigits` fixes the decimals; `maxFractionDigits` trims trailing zeros
 * (exchange rates arrive as `4.1779580000` and read as `4.177958`).
 */
export function formatNumber(
  value: Numeric,
  options: { fractionDigits?: number; maxFractionDigits?: number } = {},
): string {
  const n = toDecimalString(value);
  if (n === null) return EMPTY;
  const { fractionDigits, maxFractionDigits } = options;
  const digits: Intl.NumberFormatOptions =
    fractionDigits !== undefined
      ? { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }
      : maxFractionDigits !== undefined
        ? { maximumFractionDigits: maxFractionDigits }
        : {};
  return numberFormat(digits).format(n as DecimalString);
}

/** `28.9K`, `1.2M`: for axis ticks, tiles and dense cells. */
export function formatCompact(value: Numeric): string {
  const n = toDecimalString(value);
  return n === null
    ? EMPTY
    : numberFormat({ notation: 'compact', maximumFractionDigits: 1 }).format(n as DecimalString);
}

/** A 0–1 fraction as a percentage. For RTP use `formatRtp`, which also flags unit errors upstream. */
export function formatPercent(
  fraction: number | null | undefined,
  options: { fractionDigits?: number; signDisplay?: Intl.NumberFormatOptions['signDisplay'] } = {},
): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return EMPTY;
  const { fractionDigits = 1, signDisplay } = options;
  return numberFormat({
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    ...(signDisplay ? { signDisplay } : {}),
  }).format(fraction);
}

const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?)?(Z|[+-]\d{2}:?\d{2})?$/i;

/**
 * Timestamps as the API sends them. Zone-less `YYYY-MM-DD HH:mm:ss` (MySQL DATETIME) is UTC.
 * Only ISO-shaped strings are accepted, so a stray number-like string never becomes a date.
 */
export function parseTimestamp(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  let date: Date;
  if (value instanceof Date) date = new Date(value.getTime());
  else if (typeof value === 'number') date = new Date(value);
  else {
    const s = value.trim();
    const match = ISO_TIMESTAMP.exec(s);
    if (!match) return null;
    const hasTime = s.length > 10;
    date = new Date(hasTime && !match[1] ? `${s.replace(' ', 'T')}Z` : s);
  }
  return Number.isNaN(date.getTime()) ? null : date;
}

const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();
function parts(date: Date, tz: TimeZonePref, zoneName?: 'shortOffset'): Record<string, string> {
  const key = `${tz}:${zoneName ?? ''}`;
  let f = dateTimeFormats.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(LOCALE, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
      ...(tz === 'utc' ? { timeZone: 'UTC' } : {}),
      ...(zoneName ? { timeZoneName: zoneName } : {}),
    });
    dateTimeFormats.set(key, f);
  }
  return Object.fromEntries(f.formatToParts(date).map((p) => [p.type, p.value]));
}

export interface DateTimeOptions {
  tz?: TimeZonePref;
  seconds?: boolean;
  /** Append the zone (`UTC`, `GMT+2`) where no column header says it. */
  withZone?: boolean;
}

/** Sortable `2026-09-26 14:03`; UTC unless the user prefers local time. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  options: DateTimeOptions = {},
): string {
  const date = parseTimestamp(value);
  if (!date) return EMPTY;
  const { tz = 'utc', seconds = false, withZone = false } = options;
  const p = parts(date, tz, withZone && tz === 'local' ? 'shortOffset' : undefined);
  const time = `${p.hour}:${p.minute}${seconds ? `:${p.second}` : ''}`;
  const zone = withZone ? ` ${tz === 'utc' ? 'UTC' : (p.timeZoneName ?? '')}`.trimEnd() : '';
  return `${p.year}-${p.month}-${p.day} ${time}${zone}`;
}

export function formatDate(
  value: string | number | Date | null | undefined,
  options: { tz?: TimeZonePref } = {},
): string {
  const date = parseTimestamp(value);
  if (!date) return EMPTY;
  const p = parts(date, options.tz ?? 'utc');
  return `${p.year}-${p.month}-${p.day}`;
}

/** For column headers: `UTC` or `Local (Europe/Berlin)`. */
export function timeZoneLabel(tz: TimeZonePref): string {
  return tz === 'utc' ? 'UTC' : `Local (${Intl.DateTimeFormat().resolvedOptions().timeZone})`;
}

/** `3 minutes ago`, `in 1 day`. */
export function formatRelative(value: string | number | Date | null | undefined, now: Date = new Date()): string {
  const date = parseTimestamp(value);
  return date ? formatDistanceStrict(date, now, { addSuffix: true }) : EMPTY;
}
